import type { JSX } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

// v3.2 app shell (VITE_APP_REDESIGN_ENABLED): whether the desktop sidebar is
// hidden. Remembered per browser; storage can be unavailable (private
// mode), and the sidebar then starts open.
export const V3_SIDEBAR_STORAGE_KEY = "rosc.v3.sidebar";

const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(V3_SIDEBAR_STORAGE_KEY) === "hidden";
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(
      V3_SIDEBAR_STORAGE_KEY,
      collapsed ? "hidden" : "shown",
    );
  } catch {
    // A convenience only.
  }
};

interface ShellState {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

const ShellContext = createContext<ShellState | null>(null);

export const V3ShellProvider = ({
  children,
  initialCollapsed,
}: {
  children: React.ReactNode;
  // Stories and tests.
  initialCollapsed?: boolean;
}): JSX.Element => {
  const [collapsed, setState] = useState(
    () => initialCollapsed ?? readCollapsed(),
  );
  const setCollapsed = useCallback((next: boolean) => {
    setState(next);
    writeCollapsed(next);
  }, []);
  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggle: () => setCollapsed(!collapsed),
    }),
    [collapsed, setCollapsed],
  );
  return (
    <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
  );
};

const fallback: ShellState = {
  collapsed: false,
  setCollapsed: () => {},
  toggle: () => {},
};

// Outside the provider (the classic layout) the sidebar never collapses.
export const useV3Shell = (): ShellState =>
  useContext(ShellContext) ?? fallback;
